import { useEffect, useMemo, useState, useTransition } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  useDriversBulkActions,
  useModalConfirm,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  ConfirmResult,
  createSuccessResult,
  DriverApi,
  errorAndToastSimpleResult,
  IExtendableDto,
  IPassengersSendMessage,
  SimpleResult,
  SortEnumAPI,
} from "@ryde/shared";

import { BulkDriversMessageFormFieldsEnum } from "./bulk-drivers-message.models";
import { useBulkDriversMessageValidation } from "./use-bulk-drivers-message.validation";

interface IUseBulkDriversMessageProps {
  selectedDriverIds: number[];
}

export const useBulkDriversMessageDrawer = ({
  selectedDriverIds,
}: IUseBulkDriversMessageProps) => {
  const { t } = useTranslation();
  const [drivers, setDrivers] = useState<IExtendableDto[]>([]);
  const [isLoading, startTransition] = useTransition();
  const { bulkSendMessageToDrivers } = useDriversBulkActions();
  const bulkDriversMessageResolver =
    useBulkDriversMessageValidation().bulkDriversMessageResolver;

  const resolver = useYupValidationResolver(
    bulkDriversMessageResolver,
    "driver",
  );

  const { confirmDialogResult } = useModalConfirm();

  const methods = useForm<IPassengersSendMessage>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      subject: "",
      preview: "",
      message: "",
      isEmail: true,
      isPush: false,
      isSms: false,
    },
    resolver,
  });

  const {
    watch,
    getValues,
    formState: { isValid },
  } = methods;

  const isSendViaEmailValueWatch = watch(
    BulkDriversMessageFormFieldsEnum.isEmail,
  );
  const isSendViaSmsValueWatch = watch(BulkDriversMessageFormFieldsEnum.isSms);
  const isSendViaPushValueWatch = watch(
    BulkDriversMessageFormFieldsEnum.isPush,
  );

  const isSaveDisabled = useMemo(() => {
    const isAtLeastOneMethodChosen =
      isSendViaEmailValueWatch ||
      isSendViaSmsValueWatch ||
      isSendViaPushValueWatch;
    return isLoading || !isValid || !isAtLeastOneMethodChosen;
  }, [
    isSendViaEmailValueWatch,
    isSendViaPushValueWatch,
    isSendViaSmsValueWatch,
    isLoading,
    isValid,
  ]);

  const onSaveClick = useEvent(async (): Promise<void> => {
    if (!isValid) {
      return;
    }

    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t("general.SEND_MESSAGES_TO_DRIVERS_MESSAGE"),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    const formFields = getValues();

    bulkSendMessageToDrivers({
      ...formFields,
      to: selectedDriverIds,
    });
  });

  const fetchDriversOnInit = useEvent(
    async (driverIds: number[]): Promise<SimpleResult<void>> => {
      const body = {
        where: { id: { $in: driverIds } },
        sort: SortEnumAPI.SortById,
      } as unknown as Parameters<typeof DriverApi.searchDriverByCriteria>[0];
      const result = await DriverApi.searchDriverByCriteria(body);

      if (result && result.success && result.data) {
        setDrivers(result.data);
      } else if (result) {
        errorAndToastSimpleResult(result, t);
      }

      return createSuccessResult();
    },
  );

  useEffect(() => {
    if (selectedDriverIds?.length) {
      startTransition(() => {
        fetchDriversOnInit(selectedDriverIds);
      });
    }
  }, [fetchDriversOnInit, selectedDriverIds]);

  return {
    methods,
    isSaveDisabled,
    drivers,
    isLoading,
    onSaveClick,
  };
};
